import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Switch } from '../../../components/ui/Switch';
import { Badge } from '../../../components/ui/Badge';
import { Shield, ShieldAlert, Save, KeyRound, Copy, Check } from 'lucide-react';
import { TenantDetail } from '../../../types/domain';

interface TenantProfileCardProps {
  tenant: TenantDetail;
  fullName: string;
  setFullName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  isSuperadmin: boolean;
  setIsSuperadmin: (superadmin: boolean) => void;
  isSaving: boolean;
  isSelf: boolean;
  onSave: (e: React.FormEvent) => void;
  onOpenResetModal: () => void;
}

export const TenantProfileCard: React.FC<TenantProfileCardProps> = ({
  tenant,
  fullName,
  setFullName,
  email,
  setEmail,
  isActive,
  setIsActive,
  isSuperadmin,
  setIsSuperadmin,
  isSaving,
  isSelf,
  onSave,
  onOpenResetModal,
}) => {
  const [copiedId, setCopiedId] = React.useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(tenant.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Shield size={16} className="text-cf-orange" />
          <span>User Profile & Access Control</span>
        </CardTitle>
        <Badge variant={tenant.is_active ? 'success' : 'error'}>
          {tenant.is_active ? 'Active' : 'Suspended'}
        </Badge>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSave} className="space-y-4">
          <div className="flex items-center justify-between p-2 rounded bg-cf-bg border border-cf-border text-xs font-mono">
            <span className="text-cf-textMuted">User ID:</span>
            <div className="flex items-center gap-1.5">
              <span className="text-cf-textPrimary truncate max-w-[200px] sm:max-w-none">{tenant.id}</span>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={copyId}
                title="Copy User ID"
              >
                {copiedId ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. John Doe"
              required
            />
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="pt-2 border-t border-cf-border space-y-3">
            <Switch
              checked={isActive}
              onChange={setIsActive}
              disabled={isSelf}
              label="Account Active"
              description={
                isSelf
                  ? 'You cannot suspend your own account.'
                  : 'Suspended users cannot log in or authenticate API requests.'
              }
            />

            <Switch
              checked={isSuperadmin}
              onChange={setIsSuperadmin}
              disabled={isSelf}
              label="Platform Super-Administrator"
              description={
                isSelf
                  ? 'You cannot demote yourself from super-administrator.'
                  : 'Grants platform-wide control across all tenants, organizations, and system telemetry.'
              }
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-cf-border">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<KeyRound size={13} />}
              onClick={onOpenResetModal}
            >
              Reset Password
            </Button>

            <Button
              type="submit"
              variant="primary"
              size="sm"
              isLoading={isSaving}
              leftIcon={<Save size={13} />}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
